import {
  MAX_SELECTED_SKILLS,
  type GroupedSkills,
  type SelectedSkillReference,
  type SkillOrigin,
  type SkillSourceFilterValue,
  type SkillSummary,
} from "./types";

function appendUniqueBySlug<T extends SkillSummary>(
  target: T[],
  candidates: readonly T[],
  seenSlugs: Set<string>
): void {
  for (const skill of candidates) {
    if (!skill.enabled || seenSlugs.has(skill.slug)) continue;
    seenSlugs.add(skill.slug);
    target.push(skill);
  }
}

/**
 * Build the invokable catalog in Web's priority order. The first enabled
 * record for a slug wins, and order inside every API response is retained.
 */
export function groupSkillsByPriority<T extends SkillSummary>(
  personal: readonly T[],
  directory: readonly T[]
): GroupedSkills<T> {
  const groups: GroupedSkills<T> = {
    personal: [],
    organization: [],
    finster: [],
  };
  const seenSlugs = new Set<string>();

  appendUniqueBySlug(groups.personal, personal, seenSlugs);
  appendUniqueBySlug(
    groups.organization,
    directory.filter((skill) => skill.scope === "org"),
    seenSlugs
  );
  appendUniqueBySlug(
    groups.finster,
    directory.filter((skill) => skill.scope !== "org"),
    seenSlugs
  );
  return groups;
}

export function flattenSkillsByPriority<T extends SkillSummary>(
  personal: readonly T[],
  directory: readonly T[]
): Array<T & { origin: SkillOrigin }> {
  const groups = groupSkillsByPriority(personal, directory);
  return [
    ...groups.personal.map((skill) => ({
      ...skill,
      origin: "personal" as const,
    })),
    ...groups.organization.map((skill) => ({
      ...skill,
      origin: "organization" as const,
    })),
    ...groups.finster.map((skill) => ({
      ...skill,
      origin: "finster" as const,
    })),
  ];
}

export function getSkillOrigin(skill: SkillSummary): SkillOrigin {
  if (skill.origin) return skill.origin;
  if (skill.source !== "directory") return "personal";
  if (skill.scope === "org") return "organization";
  return "finster";
}

export function filterSkillsBySource<T extends SkillSummary>(
  skills: readonly T[],
  sourceFilter: SkillSourceFilterValue
): T[] {
  if (sourceFilter === "all") return [...skills];
  if (sourceFilter === "personal") {
    return skills.filter((skill) => getSkillOrigin(skill) === "personal");
  }
  return skills.filter((skill) => getSkillOrigin(skill) !== "personal");
}

/** Literal, case-insensitive search over endpoint display metadata only. */
export function filterSkillsByQuery<T extends SkillSummary>(
  skills: readonly T[],
  query: string
): T[] {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return [...skills];

  return skills.filter((skill) =>
    [skill.name, skill.slug, skill.description].some((value) =>
      value.toLowerCase().includes(normalizedQuery)
    )
  );
}

export function findEnabledSkillByPath<T extends SkillSummary>(
  skills: readonly T[],
  path: string
): T | undefined {
  const slug = path.trim().replace(/^\/+/, "");
  if (!slug) return undefined;
  return skills.find((skill) => skill.enabled && skill.slug === slug);
}

function isSelectedSkillReference(value: unknown): value is SelectedSkillReference {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const skill = value as Partial<SelectedSkillReference>;
  return (
    typeof skill.id === "string" &&
    skill.id.trim().length > 0 &&
    typeof skill.name === "string" &&
    skill.name.trim().length > 0 &&
    typeof skill.slug === "string" &&
    skill.slug.trim().length > 0
  );
}

/**
 * Preserve first-seen selection order while enforcing the backend cap and
 * removing duplicate IDs or slugs. Other metadata is deliberately ignored.
 */
export function normalizeSelectedSkills(
  skills?: unknown,
  legacySkill?: unknown
): SelectedSkillReference[] {
  const candidates = Array.isArray(skills) ? skills : [legacySkill];
  const normalized: SelectedSkillReference[] = [];
  const seenIds = new Set<string>();
  const seenSlugs = new Set<string>();

  for (const candidate of candidates) {
    if (!isSelectedSkillReference(candidate)) continue;
    if (seenIds.has(candidate.id) || seenSlugs.has(candidate.slug)) continue;
    normalized.push(candidate);
    seenIds.add(candidate.id);
    seenSlugs.add(candidate.slug);
    if (normalized.length === MAX_SELECTED_SKILLS) break;
  }
  return normalized;
}

export const normalizeSkillSelection = normalizeSelectedSkills;
